import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock } from "@/components/Upgrade";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/plant";
import { toast } from "sonner";
import { shareNodeAsPng } from "@/lib/shareImage";
import { CompanionHint } from "@/components/CompanionHint";
import { SnapshotsDialog } from "@/components/SnapshotsDialog";
import { ShareCard } from "@/components/ShareCard";
import { PlanPrint } from "@/components/PlanPrint";
import { checkBed, inPot, neighbourTint, gapCells } from "@/lib/gardenChecks";
import { autoArrange, stats as arrangeStats } from "@/lib/autoArrange";
import {
  LayoutGrid, Plus, ArrowLeft, Save, Trash2, Eraser, Home, Sprout, Trees,
  AlertTriangle, CalendarRange, Loader2, Ruler, Sparkles, Scissors, Leaf, Snowflake, Sun, X,
  Share2, Target, CheckCircle2, Printer, Flower2, Grid3x3, HeartHandshake, History, RotateCcw, Wand2,
} from "lucide-react";

const BED_TYPES = {
  raised: { label: "Raised Bed", icon: Home, tint: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  polytunnel: { label: "Polytunnel", icon: Trees, tint: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300" },
  open: { label: "Open Ground", icon: Sprout, tint: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  container: { label: "Container", icon: Flower2, tint: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300" },
  tray: { label: "Seed Tray", icon: Grid3x3, tint: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300" },
};

const CELL = 46;

function dimLabel(n, cellSize, unit) {
  const total = n * cellSize;
  if (unit === "imperial") {
    const ft = total / 12;
    return `${total}in (${ft % 1 === 0 ? ft : ft.toFixed(1)}ft)`;
  }
  const m = total / 100;
  return `${total}cm (${m % 1 === 0 ? m : m.toFixed(1)}m)`;
}

/* ---------------- Grid for one bed ---------------- */
function BedGrid({ bed, vegBySlug, tool, selectedSlug, onPaint, crowded = new Set(), clash = new Set(), readOnly = false, testPrefix = "cell" }) {
  const sel = tool === "plant" && selectedSlug ? vegBySlug[selectedSlug] : null;
  const placed = useMemo(() => {
    const m = {};
    (bed.placements || []).forEach((p) => { m[`${p.col}-${p.row}`] = p; });
    return m;
  }, [bed.placements]);

  const cells = [];
  for (let r = 0; r < bed.rows; r++) {
    for (let c = 0; c < bed.cols; c++) {
      const p = placed[`${c}-${r}`];
      const veg = p ? vegBySlug[p.slug] : null;
      if (!inPot(bed, c, r)) { cells.push(<div key={`${c}-${r}`} style={{ width: CELL, height: CELL }} />); continue; }
      const bad = crowded.has(`${c}-${r}`);
      const repeat = clash.has(`${c}-${r}`);
      const tint = sel && !p ? neighbourTint(sel, c, r, (x, y) => { const q = placed[`${x}-${y}`]; return q && vegBySlug[q.slug]; }) : null;
      cells.push(
        <button
          key={`${c}-${r}`}
          onClick={() => !readOnly && onPaint(bed.id, c, r)}
          disabled={readOnly}
          data-testid={`${testPrefix}-${bed.id}-${c}-${r}`}
          data-clash={repeat || undefined}
          data-tint={tint || undefined}
          title={veg ? veg.name : "Empty"}
          className={`relative border ${bad ? "border-rose-500 ring-2 ring-inset ring-rose-400" : "border-emerald-900/10 dark:border-white/10"} hover:ring-2 hover:ring-emerald-500 hover:z-10 transition-all flex items-center justify-center`}
          style={{
            width: CELL, height: CELL,
            backgroundColor: repeat ? "rgba(239,68,68,0.45)" : veg ? `${veg.family_color}22` : tint === "bad" ? "rgba(244,63,94,0.28)" : tint === "good" ? "rgba(16,185,129,0.28)" : "transparent",
            cursor: readOnly ? "default" : tool === "erase" ? "cell" : tool === "track" ? "pointer" : selectedSlug ? "copy" : "default",
          }}
        >
          {veg && (
            <img src={veg.icon} alt={veg.name} className="w-[82%] h-[82%] object-contain pointer-events-none drop-shadow-sm" />
          )}
          {p?.plant_id && (
            <span className="absolute -top-0.5 -right-0.5 bg-emerald-600 rounded-full p-[1px] shadow" title="Tracked as a sprout">
              <CheckCircle2 className="h-3 w-3 text-white" />
            </span>
          )}
        </button>
      );
    }
  }

  return (
    <div className="overflow-x-auto pb-1">
      <div
        className={`grid ${bed.type === "container" ? "rounded-full p-1" : "rounded-lg"} overflow-hidden border-2 border-emerald-900/20 dark:border-white/15 bg-[repeating-linear-gradient(45deg,#f5f0e6_0_6px,#efe8d8_6px_12px)] dark:bg-[repeating-linear-gradient(45deg,#1f2a22_0_6px,#243029_6px_12px)]`}
        style={{ gridTemplateColumns: `repeat(${bed.cols}, ${CELL}px)`, width: bed.cols * CELL + (bed.type === "container" ? 8 : 0) }}
        data-testid={`${readOnly ? "snap-" : ""}bed-grid-${bed.id}`}
      >
        {cells}
      </div>
    </div>
  );
}

/* ---------------- Add Bed dialog ---------------- */
const clamp = (n) => Math.max(1, Math.min(40, n));

function AddBedDialog({ onAdd, cellSize, unit }) {
  const u = unit === "imperial" ? "in" : "cm";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("raised");
  const [w, setW] = useState(unit === "imperial" ? 48 : 120);
  const [l, setL] = useState(unit === "imperial" ? 96 : 240);
  const [diam, setDiam] = useState(unit === "imperial" ? 24 : 60);
  const [cols, setCols] = useState(6);
  const [rows, setRows] = useState(4);

  const size = () => {
    if (type === "tray") return { cols: clamp(parseInt(cols) || 1), rows: clamp(parseInt(rows) || 1) };
    if (type === "container") { const n = clamp(Math.round((parseFloat(diam) || 1) / cellSize) || 1); return { cols: n, rows: n }; }
    return { cols: clamp(Math.ceil((parseFloat(w) || 1) / cellSize)), rows: clamp(Math.ceil((parseFloat(l) || 1) / cellSize)) };
  };
  const dims = size();

  const submit = () => {
    onAdd({ name: name.trim() || BED_TYPES[type].label, type, ...dims });
    setOpen(false);
    setName(""); setType("raised");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" className="rounded-xl" data-testid="add-bed-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add bed / pot / tray
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-bed-dialog">
        <DialogHeader>
          <DialogTitle>Add a growing area</DialogTitle>
          <DialogDescription>Raised beds, containers and seed trays can all live in one layout.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. South raised bed" data-testid="bed-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="bed-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(BED_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`bed-type-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {type === "tray" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Cells across</Label><Input type="number" min={1} max={40} value={cols} onChange={(e) => setCols(e.target.value)} data-testid="bed-cols-input" /></div>
              <div className="space-y-1.5"><Label>Cells down</Label><Input type="number" min={1} max={40} value={rows} onChange={(e) => setRows(e.target.value)} data-testid="bed-rows-input" /></div>
            </div>
          ) : type === "container" ? (
            <div className="space-y-1.5"><Label>Pot diameter ({u})</Label><Input type="number" min={1} value={diam} onChange={(e) => setDiam(e.target.value)} data-testid="bed-diameter-input" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Width ({u})</Label><Input type="number" min={1} value={w} onChange={(e) => setW(e.target.value)} data-testid="bed-width-input" /></div>
              <div className="space-y-1.5"><Label>Length ({u})</Label><Input type="number" min={1} value={l} onChange={(e) => setL(e.target.value)} data-testid="bed-length-input" /></div>
            </div>
          )}
          <p className="text-xs text-muted-foreground" data-testid="bed-size-preview">
            {type === "tray" ? `${dims.cols * dims.rows} cells — one seedling per cell.` : `Grid: ${dims.cols} × ${dims.rows} squares of ${cellSize}${u}.`}
          </p>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="confirm-add-bed">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Schedule dialog ---------------- */
function ScheduleDialog({ planId, open, onOpenChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/garden/plans/${planId}/schedule`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load schedule"))
      .finally(() => setLoading(false));
  }, [open, planId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-emerald-600" /> Planting schedule</DialogTitle>
          <DialogDescription>Sow, transplant &amp; harvest dates for every crop in this plan, based on your frost date.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : data?.needs_frost_date ? (
          <div className="text-center py-8">
            <Snowflake className="h-8 w-8 text-sky-400 mx-auto mb-2" />
            <p className="text-muted-foreground mb-4">Set your last frost date to generate exact dates.</p>
            <Button onClick={() => navigate("/calendar")} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="schedule-set-frost">Set frost date</Button>
          </div>
        ) : !data || (data.items.length === 0 && data.unmatched.length === 0) ? (
          <p className="text-muted-foreground text-center py-8">Plant some crops on the grid to see their schedule.</p>
        ) : (
          <div className="space-y-2.5" data-testid="schedule-list">
            {data.items.map((it) => (
              <div key={it.slug} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`schedule-item-${it.slug}`}>
                <img src={it.icon} alt="" className="h-10 w-10 object-contain shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm flex items-center gap-2">{it.name} <span className="text-xs text-muted-foreground font-normal">×{it.count}</span></p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                    <span className="inline-flex items-center gap-1"><Sprout className="h-3 w-3 text-emerald-600" /> Sow {it.sow_date ? formatDate(it.sow_date) : (it.note ? "autumn" : "—")}</span>
                    {it.method === "indoors" && it.transplant_date && (
                      <span className="inline-flex items-center gap-1"><Leaf className="h-3 w-3 text-violet-600" /> Transplant {formatDate(it.transplant_date)}</span>
                    )}
                    {it.harvest_date && <span className="inline-flex items-center gap-1"><Scissors className="h-3 w-3 text-orange-600" /> Harvest ~{formatDate(it.harvest_date)}</span>}
                  </div>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${it.season === "warm" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300" : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"}`}>
                  {it.season === "warm" ? <Sun className="h-3 w-3" /> : <Snowflake className="h-3 w-3" />} {it.season}
                </span>
              </div>
            ))}
            {data.unmatched.length > 0 && (
              <p className="text-xs text-muted-foreground pt-2">No schedule rule for: {data.unmatched.map((u) => u.name).join(", ")}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ================================================== Main */
export default function GardenPlanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [families, setFamilies] = useState({});
  const [loading, setLoading] = useState(true);

  const [current, setCurrent] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [tool, setTool] = useState("plant"); // plant | erase | track
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newName, setNewName] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);
  const [printSchedule, setPrintSchedule] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [showSnaps, setShowSnaps] = useState(false);
  const [compareSnap, setCompareSnap] = useState(null);
  const [preview, setPreview] = useState(null);

  const vegBySlug = useMemo(() => {
    const m = {};
    catalog.forEach((v) => { m[v.slug] = v; });
    return m;
  }, [catalog]);

  const loadAll = useCallback(async () => {
    try {
      const [p, t, c] = await Promise.all([
        api.get("/garden/plans"),
        user?.is_premium ? api.get("/garden/templates") : Promise.resolve({ data: { templates: [] } }),
        api.get("/garden/catalog"),
      ]);
      setPlans(p.data);
      setTemplates(t.data.templates);
      setCatalog(c.data.vegetables);
      setFamilies(c.data.families);
    } catch {
      toast.error("Could not load your layouts");
    } finally {
      setLoading(false);
    }
  }, [user?.is_premium]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const [params, setParams] = useSearchParams();
  const planParam = params.get("plan");
  useEffect(() => {
    if (!planParam || loading) return;
    api.get(`/garden/plans/${planParam}`).then(({ data }) => { setCurrent(data); setDirty(false); }).catch(() => toast.error("Could not open plan"));
    setParams({}, { replace: true });
  }, [planParam, loading, setParams]);

  if (user && !user.is_premium && !loading && plans.length === 0 && !current && !planParam) {
    return (
      <Layout>
        <PremiumLock
          title="Garden Layout is a Pro feature"
          description="Design scaled bed layouts, drop in crops, and get automatic rotation warnings and planting dates. Free accounts can turn their dashboard garden grid into one Layout plan."
          features={["Multi-bed scaled grid designer", "Crop rotation warnings by family", "Auto planting schedule from your frost date"]}
        />
      </Layout>
    );
  }

  /* ---------- plan ops ---------- */
  const openPlan = async (id) => {
    try {
      const { data } = await api.get(`/garden/plans/${id}`);
      setCurrent(data);
      setDirty(false);
      setSelectedSlug(null);
      setTool("plant");
      setCompareSnap(null);
      setPreview(null);
    } catch {
      toast.error("Could not open plan");
    }
  };

  const createPlan = async (templateId) => {
    const name = (newName.trim() || (templateId ? templates.find((t) => t.id === templateId)?.name : "My Garden") || "My Garden");
    try {
      const body = templateId
        ? { name, unit: "metric", cell_size: 30, beds: [] }
        : { name, unit: "metric", cell_size: 30, beds: [{ id: crypto.randomUUID(), name: "Bed 1", type: "raised", cols: 4, rows: 6, prev_family: "", placements: [] }] };
      const url = templateId ? `/garden/plans?template_id=${templateId}` : "/garden/plans";
      const { data } = await api.post(url, body);
      setNewName("");
      await loadAll();
      setCurrent(data);
      setDirty(false);
      toast.success("Plan created");
    } catch {
      toast.error("Could not create plan");
    }
  };

  const savePlan = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/garden/plans/${current.id}`, {
        name: current.name, unit: current.unit, cell_size: current.cell_size, beds: current.beds,
      });
      setCurrent(data);
      setDirty(false);
      toast.success("Plan saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async () => {
    if (!current) return;
    try {
      await api.delete(`/garden/plans/${current.id}`);
      setCurrent(null);
      await loadAll();
      toast.success("Plan deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const openSchedule = async () => {
    if (dirty) await savePlan();
    setShowSchedule(true);
  };

  /* ---------- bed/cell ops ---------- */
  const updateBed = (bedId, fn) => {
    setCurrent((prev) => ({ ...prev, beds: prev.beds.map((b) => (b.id === bedId ? fn(b) : b)) }));
    setDirty(true);
  };

  const paint = (bedId, col, row) => {
    updateBed(bedId, (b) => {
      const others = (b.placements || []).filter((p) => !(p.col === col && p.row === row));
      if (tool === "erase") return { ...b, placements: others };
      if (!selectedSlug) return b;
      return { ...b, placements: [...others, { id: crypto.randomUUID(), slug: selectedSlug, col, row }] };
    });
  };

  const trackCell = async (bedId, col, row) => {
    const bed = current.beds.find((b) => b.id === bedId);
    const pl = bed?.placements.find((p) => p.col === col && p.row === row);
    if (!pl) { toast.info("Plant a crop in this square first, then tap to track it."); return; }
    if (pl.plant_id) { toast("Already tracked as a sprout", { action: { label: "View", onClick: () => navigate(`/sprouts/${pl.plant_id}`) } }); return; }
    const veg = vegBySlug[pl.slug];
    try {
      const { data } = await api.post("/plants", {
        name: veg?.name || pl.slug,
        species: veg?.name || "",
        location: bed.name,
        planted_date: new Date().toISOString().slice(0, 10),
        notes: `From garden plan "${current.name}"`,
      });
      const newBeds = current.beds.map((b) => (b.id === bedId
        ? { ...b, placements: b.placements.map((p) => (p.col === col && p.row === row ? { ...p, plant_id: data.id } : p)) }
        : b));
      await api.put(`/garden/plans/${current.id}`, { name: current.name, unit: current.unit, cell_size: current.cell_size, beds: newBeds });
      setCurrent((prev) => ({ ...prev, beds: newBeds }));
      setDirty(false);
      toast.success(`${veg?.name || "Sprout"} added to your garden`, { action: { label: "View", onClick: () => navigate(`/sprouts/${data.id}`) } });
    } catch {
      toast.error("Could not track this plant");
    }
  };

  const handleCell = (bedId, col, row) => {
    if (tool === "track") { trackCell(bedId, col, row); return; }
    paint(bedId, col, row);
  };

  const iconUrl = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/garden/icon/${slug}`;

  const handleShare = async () => {
    if (dirty) await savePlan();
    setSharing(true);
    try {
      await shareNodeAsPng(shareRef.current, current.name || "garden-plan");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create the image. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const handlePrint = async () => {
    if (dirty) await savePlan();
    setPrinting(true);
    try {
      const { data } = await api.get(`/garden/plans/${current.id}/schedule`);
      setPrintSchedule(data);
    } catch {
      setPrintSchedule(null);
    }
    await new Promise((r) => setTimeout(r, 700));
    setPrinting(false);
    window.print();
  };

  const startArrange = (bed) => {
    if (!user?.is_premium) {
      toast("Auto Arrange is a Pro feature", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    const items = bed.placements || [];
    if (items.length < 2) { toast.info("Plant at least two crops in this bed first."); return; }
    const gap = bed.type === "tray" ? null : (v) => gapCells(v, current.cell_size, current.unit);
    const next = autoArrange(items, vegBySlug, gap);
    setPreview({ bedId: bed.id, placements: next, before: arrangeStats(items, vegBySlug), after: arrangeStats(next, vegBySlug) });
  };

  const applyArrange = () => {
    const bed = current.beds.find((b) => b.id === preview.bedId);
    const prev = bed.placements;
    updateBed(preview.bedId, (b) => ({ ...b, placements: preview.placements }));
    const id = preview.bedId;
    setPreview(null);
    toast.success("Bed rearranged — remember to save", { action: { label: "Undo", onClick: () => updateBed(id, (b) => ({ ...b, placements: prev })) } });
  };

  const addBed = (bed) => {
    setCurrent((prev) => ({ ...prev, beds: [...prev.beds, { id: crypto.randomUUID(), prev_family: "", placements: [], ...bed }] }));
    setDirty(true);
  };

  const removeBed = (bedId) => {
    setCurrent((prev) => ({ ...prev, beds: prev.beds.filter((b) => b.id !== bedId) }));
    setDirty(true);
  };

  const setPrevFamily = (bedId, val) => updateBed(bedId, (b) => ({ ...b, prev_family: val === "none" ? "" : val }));

  /* ---------- rotation warnings (client-side, instant) ---------- */
  const bedWarnings = (bed) => {
    const warns = [];
    const famCounts = {};
    (bed.placements || []).forEach((p) => {
      const fk = vegBySlug[p.slug]?.family_key;
      if (fk) famCounts[fk] = (famCounts[fk] || 0) + 1;
    });
    const total = (bed.placements || []).length;
    if (bed.prev_family && famCounts[bed.prev_family]) {
      warns.push(`You grew ${families[bed.prev_family]?.name || "this family"} here last season — rotate to a different family to reduce pests & disease.`);
    }
    Object.entries(famCounts).forEach(([fk, n]) => {
      if (total >= 4 && n / total > 0.6) {
        warns.push(`${families[fk]?.name || fk} fills most of this bed — mixing families improves soil health and lowers pest pressure.`);
      }
    });
    return warns;
  };

  /* ======================= RENDER ======================= */
  if (loading) {
    return <Layout><div className="h-64 rounded-2xl bg-card border border-border animate-pulse" /></Layout>;
  }

  /* ------- LIST VIEW ------- */
  if (!current) {
    return (
      <Layout>
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-emerald-600" /> Garden Layout
          </h1>
          <p className="text-muted-foreground mt-1">Design beds, containers and seed trays — with spacing, companion and rotation checks.</p>
        </div>

        {!user?.is_premium && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="free-layout-note">
            <p className="text-sm text-amber-900 dark:text-amber-200">Free accounts get one Layout plan made from your dashboard grid. Upgrade to Pro for unlimited plans and templates.</p>
            <Button onClick={() => navigate("/pricing")} size="sm" className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="free-layout-upgrade">Upgrade</Button>
          </div>
        )}
        {user?.is_premium && (<>
        {/* New plan */}
        <div className="bg-card rounded-2xl border border-border p-5 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>Start a new plan</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Plan name (e.g. 2026 Veg Patch)" data-testid="new-plan-name" onKeyDown={(e) => e.key === "Enter" && createPlan(null)} />
            </div>
            <Button onClick={() => createPlan(null)} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="create-blank-plan">
              <Plus className="h-4 w-4 mr-1.5" /> Blank plan
            </Button>
          </div>
        </div>

        {/* Templates */}
        <h2 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Start from a template</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {templates.map((t) => (
            <button key={t.id} onClick={() => createPlan(t.id)} data-testid={`template-${t.id}`}
              className="text-left bg-card rounded-2xl border border-border p-5 shadow-sm hover:shadow-md hover:border-emerald-400 transition group">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mb-3 group-hover:scale-105 transition">
                <LayoutGrid className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
              </div>
              <h3 className="font-display font-semibold">{t.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-2 font-medium">{t.beds.length} bed{t.beds.length > 1 ? "s" : ""} · {t.unit}</p>
            </button>
          ))}
        </div>
        </>)}

        {/* Existing plans */}
        <h2 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Your plans</h2>
        {plans.length === 0 ? (
          <div className="bg-card rounded-2xl border border-dashed border-border p-10 text-center">
            <Sprout className="h-9 w-9 text-emerald-400 mx-auto mb-2" />
            <p className="text-muted-foreground">No plans yet — start blank or pick a template above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-list">
            {plans.map((p) => (
              <button key={p.id} onClick={() => openPlan(p.id)} data-testid={`plan-card-${p.id}`}
                className="text-left bg-card rounded-2xl border border-border p-5 shadow-sm hover:shadow-md hover:border-emerald-400 transition">
                <h3 className="font-display font-semibold truncate">{p.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{p.bed_count} bed{p.bed_count !== 1 ? "s" : ""} · {p.planted_count} planted · {p.unit}</p>
              </button>
            ))}
          </div>
        )}
      </Layout>
    );
  }

  /* ------- EDITOR VIEW ------- */
  const unitLabel = current.unit === "imperial" ? "in" : "cm";
  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Button variant="ghost" size="sm" onClick={() => { if (dirty && !window.confirm("Leave without saving?")) return; setCurrent(null); setCompareSnap(null); setPreview(null); }} data-testid="back-to-plans" className="rounded-xl">
          <ArrowLeft className="h-4 w-4 mr-1" /> Plans
        </Button>
        <Input
          value={current.name}
          onChange={(e) => { setCurrent((p) => ({ ...p, name: e.target.value })); setDirty(true); }}
          className="max-w-[220px] font-display font-semibold"
          data-testid="plan-name-input"
        />
        <div className="flex flex-wrap items-center gap-1.5 ml-auto w-full sm:w-auto">
          <Button onClick={handleShare} disabled={sharing} variant="secondary" className="rounded-xl" data-testid="share-plan">
            {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 sm:mr-1.5" />}<span className="hidden sm:inline">Share</span>
          </Button>
          {user?.is_premium && (
            <Button onClick={() => setShowSnaps(true)} variant="secondary" className="rounded-xl" data-testid="open-snapshots">
              <History className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Snapshots</span>
            </Button>
          )}
          <Button onClick={handlePrint} disabled={printing} variant="secondary" className="rounded-xl" data-testid="print-plan">
            {printing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Printer className="h-4 w-4 sm:mr-1.5" />}<span className="hidden sm:inline">Print</span>
          </Button>
          <Button onClick={openSchedule} variant="secondary" className="rounded-xl" data-testid="open-schedule">
            <CalendarRange className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Schedule</span>
          </Button>
          <Button onClick={savePlan} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="save-plan">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl text-destructive" data-testid="delete-plan"><Trash2 className="h-4 w-4" /></Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Delete this plan?</DialogTitle><DialogDescription>This can't be undone.</DialogDescription></DialogHeader>
              <DialogFooter><Button variant="destructive" onClick={deletePlan} data-testid="confirm-delete-plan">Delete</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Plan settings bar */}
      <div className="bg-card rounded-2xl border border-border p-4 shadow-sm mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Ruler className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Units</span>
          <Select value={current.unit} onValueChange={(v) => { setCurrent((p) => ({ ...p, unit: v })); setDirty(true); }}>
            <SelectTrigger className="h-9 w-32" data-testid="unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="metric">Metric (cm)</SelectItem>
              <SelectItem value="imperial">Imperial (in)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Cell size</span>
          <Input type="number" min={1} value={current.cell_size}
            onChange={(e) => { setCurrent((p) => ({ ...p, cell_size: Math.max(1, parseFloat(e.target.value) || 1) })); setDirty(true); }}
            className="h-9 w-20" data-testid="cell-size-input" />
          <span className="text-sm text-muted-foreground">{unitLabel} / square</span>
        </div>
        <AddBedDialog key={`${current.unit}-${current.cell_size}`} onAdd={addBed} cellSize={current.cell_size} unit={current.unit} />
      </div>

      {/* Palette */}
      <div className="bg-card rounded-2xl border border-border p-4 shadow-sm mb-5 sticky top-16 z-20">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {tool === "track" ? "Tap a planted square to track it as a sprout" : tool === "erase" ? "Tap squares to clear them" : "Pick a crop, then tap squares to plant"}</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={tool === "track" ? "default" : "outline"} onClick={() => { setTool((t) => (t === "track" ? "plant" : "track")); setSelectedSlug(null); }}
              className={`rounded-xl ${tool === "track" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} data-testid="track-tool">
              <Target className="h-4 w-4 mr-1.5" /> {tool === "track" ? "Tracking" : "Track"}
            </Button>
            <Button size="sm" variant={tool === "erase" ? "default" : "outline"} onClick={() => { setTool((t) => (t === "erase" ? "plant" : "erase")); if (tool !== "erase") setSelectedSlug(null); }}
              className={`rounded-xl ${tool === "erase" ? "bg-rose-600 hover:bg-rose-700" : ""}`} data-testid="erase-tool">
              <Eraser className="h-4 w-4 mr-1.5" /> {tool === "erase" ? "Erasing" : "Erase"}
            </Button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="veg-palette">
          {catalog.map((v) => (
            <button key={v.slug} onClick={() => { setSelectedSlug(v.slug); setTool("plant"); }}
              data-testid={`palette-${v.slug}`}
              title={`${v.name} · ${v.family} · ${v.spacing_cm}cm spacing`}
              className={`shrink-0 w-16 rounded-xl border p-1.5 flex flex-col items-center gap-0.5 transition ${selectedSlug === v.slug && tool === "plant" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-500" : "border-border hover:border-emerald-300"}`}>
              <img src={v.icon} alt={v.name} className="w-10 h-10 object-contain" />
              <span className="text-[10px] font-medium leading-tight text-center truncate w-full">{v.name}</span>
            </button>
          ))}
        </div>
        {tool === "plant" && selectedSlug && <CompanionHint veg={vegBySlug[selectedSlug]} catalog={catalog} />}
      </div>

      {compareSnap && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3" data-testid="compare-banner">
          <p className="text-sm flex items-center gap-2"><History className="h-4 w-4 text-emerald-700" /> Comparing with snapshot from <strong>{formatDate(compareSnap.created_at)}</strong>. Red squares repeat the same crop family.</p>
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setCompareSnap(null)} data-testid="close-compare"><X className="h-4 w-4 mr-1" /> Close compare</Button>
        </div>
      )}

      {/* Beds */}
      <div className="space-y-5">
        {current.beds.length === 0 && (
          <div className="bg-card rounded-2xl border border-dashed border-border p-10 text-center">
            <LayoutGrid className="h-9 w-9 text-emerald-400 mx-auto mb-2" />
            <p className="text-muted-foreground">Add a bed to start designing your layout.</p>
          </div>
        )}
        {current.beds.map((bed) => {
          const T = BED_TYPES[bed.type] || BED_TYPES.raised;
          const Icon = T.icon;
          const warns = bedWarnings(bed);
          const chk = checkBed(bed, vegBySlug, current.cell_size, current.unit);
          const snapBed = compareSnap?.beds.find((b) => b.id === bed.id);
          const clash = new Set();
          if (snapBed) {
            const past = {};
            (snapBed.placements || []).forEach((p) => { const fk = vegBySlug[p.slug]?.family_key; if (fk) past[`${p.col}-${p.row}`] = fk; });
            (bed.placements || []).forEach((p) => { const fk = vegBySlug[p.slug]?.family_key; if (fk && past[`${p.col}-${p.row}`] === fk) clash.add(`${p.col}-${p.row}`); });
          }
          return (
            <div key={bed.id} className="bg-card rounded-2xl border border-border p-4 sm:p-5 shadow-sm" data-testid={`bed-${bed.id}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${T.tint}`}><Icon className="h-3.5 w-3.5" /> {T.label}</span>
                <Input value={bed.name} onChange={(e) => updateBed(bed.id, (b) => ({ ...b, name: e.target.value }))} className="h-8 max-w-[180px] font-semibold" data-testid={`bed-name-${bed.id}`} />
                <span className="text-xs text-muted-foreground" data-testid={`bed-dims-${bed.id}`}>
                  {bed.type === "tray" ? `${bed.cols * bed.rows} cells` : bed.type === "container" ? `${dimLabel(bed.cols, current.cell_size, current.unit)} pot` : `${dimLabel(bed.cols, current.cell_size, current.unit)} × ${dimLabel(bed.rows, current.cell_size, current.unit)}`}
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="text-xs text-muted-foreground hidden sm:inline">Last season:</span>
                  <Select value={bed.prev_family || "none"} onValueChange={(v) => setPrevFamily(bed.id, v)}>
                    <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`prev-family-${bed.id}`}><SelectValue placeholder="Last season" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {Object.entries(families).map(([k, v]) => (<SelectItem key={k} value={k}>{v.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="h-8 rounded-xl" onClick={() => startArrange(bed)} data-testid={`auto-arrange-${bed.id}`} title="Auto arrange for companions">
                    <Wand2 className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Auto arrange</span>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeBed(bed.id)} data-testid={`remove-bed-${bed.id}`}><X className="h-4 w-4" /></Button>
                </div>
              </div>

              {warns.length > 0 && (
                <div className="mb-3 space-y-1.5" data-testid={`rotation-warning-${bed.id}`}>
                  {warns.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 rounded-xl p-2.5">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              )}

              {(chk.spacing.length > 0 || chk.companion.length > 0) && (
                <div className="mb-3 space-y-1.5">
                  {chk.companion.map((w, i) => (
                    <div key={`c${i}`} className="flex items-start gap-2 text-sm bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 rounded-xl p-2.5" data-testid={`companion-warning-${bed.id}`}>
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                  {chk.spacing.map((w, i) => (
                    <div key={`s${i}`} className="flex items-start gap-2 text-sm bg-orange-50 dark:bg-orange-950/30 text-orange-800 dark:text-orange-300 rounded-xl p-2.5" data-testid={`spacing-warning-${bed.id}`}>
                      <Ruler className="h-4 w-4 shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              )}
              {chk.good > 0 && (
                <p className="mb-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5" data-testid={`companion-good-${bed.id}`}>
                  <HeartHandshake className="h-3.5 w-3.5" /> {chk.good} good companion pairing{chk.good > 1 ? "s" : ""} side by side
                </p>
              )}

              {compareSnap && clash.size > 0 && (
                <div className="mb-3 flex items-start gap-2 text-sm bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 rounded-xl p-2.5" data-testid={`rotation-clash-${bed.id}`}>
                  <RotateCcw className="h-4 w-4 shrink-0 mt-0.5" /> {clash.size} square{clash.size > 1 ? "s" : ""} repeat the same crop family as {formatDate(compareSnap.created_at)} — rotate to a different family.
                </div>
              )}
              <div className={compareSnap ? "flex flex-col xl:flex-row gap-4" : ""}>
                <div className="min-w-0">
                  {compareSnap && <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Now</p>}
                  {preview?.bedId === bed.id ? (
                    <>
                      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-violet-300 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800 p-2.5 text-sm" data-testid={`arrange-preview-${bed.id}`}>
                        <Wand2 className="h-4 w-4 text-violet-600" />
                        <span className="flex-1 min-w-[200px]" data-testid={`arrange-stats-${bed.id}`}>Preview: good pairs {preview.before.good} → <strong>{preview.after.good}</strong>, bad pairs {preview.before.bad} → <strong>{preview.after.bad}</strong></span>
                        <Button size="sm" className="h-8 rounded-xl bg-emerald-700 hover:bg-emerald-800" onClick={applyArrange} data-testid={`arrange-apply-${bed.id}`}>Apply</Button>
                        <Button size="sm" variant="outline" className="h-8 rounded-xl" onClick={() => setPreview(null)} data-testid={`arrange-cancel-${bed.id}`}>Cancel</Button>
                      </div>
                      <BedGrid bed={{ ...bed, placements: preview.placements }} vegBySlug={vegBySlug} tool="view" readOnly testPrefix="preview-cell" />
                    </>
                  ) : (
                    <BedGrid bed={bed} vegBySlug={vegBySlug} tool={tool} selectedSlug={selectedSlug} onPaint={handleCell} crowded={chk.crowded} clash={clash} />
                  )}
                </div>
                {compareSnap && (
                  <div className="min-w-0 opacity-90" data-testid={`snap-bed-${bed.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{formatDate(compareSnap.created_at)}</p>
                    {snapBed ? (
                      <BedGrid bed={snapBed} vegBySlug={vegBySlug} tool="view" readOnly testPrefix="snap-cell" />
                    ) : (
                      <p className="text-sm text-muted-foreground italic">This bed wasn't in that snapshot.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Family legend */}
      <div className="mt-6 bg-card rounded-2xl border border-border p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Rotation families</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {Object.entries(families).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: v.color }} /> {v.name}
            </span>
          ))}
        </div>
      </div>

      <ScheduleDialog planId={current.id} open={showSchedule} onOpenChange={setShowSchedule} />
      {user?.is_premium && (
        <SnapshotsDialog planId={current.id} open={showSnaps} onOpenChange={setShowSnaps}
          beforeSave={async () => { if (dirty) await savePlan(); }} onCompare={setCompareSnap} />
      )}
      <PlanPrint plan={current} vegBySlug={vegBySlug} schedule={printSchedule} />
      <ShareCard ref={shareRef} plan={current} vegBySlug={vegBySlug} families={families} iconUrl={iconUrl} />
    </Layout>
  );
}
